import { ApdmUploadButton } from '@euler/app/flows/apdm/imports/ApdmUploadButton';
import { kApdmCampaignKindLabels } from '@euler/functions/apdm/campaignLabels';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import {
  ApdmBuildCardKind,
  ApdmBuildStep,
  ApdmPlanUploadReason,
} from '@euler/model/apdm';
import { FileSpreadsheet } from 'lucide-react-native';
import { memo } from 'react';
import { useAiChatActions, useAiPlanStepOpen } from '../chat/AiChatActions';
import { CardLoading, CardNotice, ChatCard } from '../components/ChatCard';
import { isAiMessageId, usePlanCard } from './usePlanCard';

const kUploadText: Record<ApdmPlanUploadReason, string> = {
  [ApdmPlanUploadReason.OwnSheet]: '上传后，我会读出表格里的客户，发给他们。',
  [ApdmPlanUploadReason.NoCustomerData]:
    '门店还没有客户数据。上传后，我会读出表格里的客户。',
};

/**
 * The who step waiting for the user's customer sheet: they upload it here,
 * and the sheet goes back as their answer.
 */
export const PlanUploadCard = memo(
  ({ messageId, streaming }: { messageId?: string; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    if (streaming || !isAiMessageId(messageId)) return null;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法上传客户表格。" />;
    return <UploadBody key={messageId} messageId={messageId} />;
  },
);

function UploadBody({ messageId }: { messageId: string }) {
  const chat = useAiChatActions();
  const interactive = useAiPlanStepOpen(ApdmBuildStep.Who);
  const state = usePlanCard(messageId, ApdmBuildCardKind.Upload);
  if (state.kind === 'loading')
    return (
      <ChatCard icon={FileSpreadsheet} title="上传客户表格" tinted>
        <CardLoading label="正在准备…" />
      </ChatCard>
    );
  if (state.kind === 'missing' || state.view.card !== ApdmBuildCardKind.Upload)
    return (
      <ChatCard icon={FileSpreadsheet} title="上传客户表格">
        <CardNotice text="没有读取到上传入口，可以用输入框旁的附件按钮发送客户表格。" />
      </ChatCard>
    );
  const card = state.view.upload;
  return (
    <ChatCard
      icon={FileSpreadsheet}
      tinted
      title="上传客户表格"
      subtitle={kApdmCampaignKindLabels[card.kind]}
    >
      <CardNotice
        text={`${
          kUploadText[card.reason]
        }支持 Excel 和 CSV，表格里要有车牌号。`}
      />
      {interactive ? (
        <ApdmUploadButton
          appearance="primary"
          label="选择客户信息文件"
          icon="upload"
          onUploaded={async info => {
            chat.send('这是我的客户表格', { attachments: [info] });
          }}
        />
      ) : null}
    </ChatCard>
  );
}
